import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { broadcastCheckInRemoval } from 'src/clinic/components/CheckIn/checkInSyncService'
import { DISPATCH_EVENT_KEYS, SIGNALING_COMMANDS } from 'src/consumer/constants'
import { sessionService } from 'src/consumer/components/LiveSessionPage/services/sessionService'
import { addWindowSignalingEventListener } from 'src/live'
import {
  getLatestIncomingEmrCheckInRequest,
  getLatestOutgoingEmrCheckInRequest,
  normalizeCheckInRequest,
  respondToEmrCheckInRequest,
  getCheckInRequestErrorMessage,
} from 'src/mentor/services/checkInRequestService'

function clearLocalCheckInState(payload = null) {
  localStorage.removeItem('checkedInDetails')
  localStorage.removeItem('peekedInRoomDetails')
  localStorage.removeItem('roomDataEventTypes')
  localStorage.removeItem('auditRoomDetails')
  broadcastCheckInRemoval()

  window.dispatchEvent(
    new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedOut, {
      detail: payload || {},
    }),
  )
}

function getRouteForRequest(requestData) {
  if (!requestData?.clinicBrandId || !requestData?.clinicLocationId) {
    return '/mentor-home/my-clinic-associations'
  }

  return `/mentor-home/my-clinic-associations/${requestData.clinicBrandId}/${requestData.clinicLocationId}`
}

export default function useMentorSignals(mentorProfileData = null) {
  const navigate = useNavigate()
  const [videoCallModalData, setVideoCallModalData] = useState(null)
  const [incomingRequestModalData, setIncomingRequestModalData] = useState(null)
  const [outgoingRequestModalData, setOutgoingRequestModalData] = useState(null)
  const [incomingRequestLoading, setIncomingRequestLoading] = useState(false)
  const [requestClockNow, setRequestClockNow] = useState(() => Date.now())

  const incomingRequestRef = useRef(null)
  const outgoingRequestRef = useRef(null)
  const redirectTimeoutRef = useRef(null)

  useEffect(() => {
    incomingRequestRef.current = incomingRequestModalData
  }, [incomingRequestModalData])

  useEffect(() => {
    outgoingRequestRef.current = outgoingRequestModalData
  }, [outgoingRequestModalData])

  useEffect(
    () => () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
      }
    },
    [],
  )

  useEffect(() => {
    if (!incomingRequestModalData && !outgoingRequestModalData) {
      return undefined
    }

    const intervalId = setInterval(() => {
      setRequestClockNow(Date.now())
    }, 1000)

    return () => clearInterval(intervalId)
  }, [incomingRequestModalData, outgoingRequestModalData])

  const restoreActiveRequests = useCallback(async () => {
    const [outgoingRequest, incomingRequest] = await Promise.all([
      getLatestOutgoingEmrCheckInRequest(),
      getLatestIncomingEmrCheckInRequest(),
    ])

    setOutgoingRequestModalData(outgoingRequest)
    setIncomingRequestModalData(incomingRequest)
  }, [])

  useEffect(() => {
    if (!mentorProfileData?.id) {
      return
    }

    restoreActiveRequests()
  }, [mentorProfileData?.id, restoreActiveRequests])

  const handleForcedCheckout = useCallback(
    (payload) => {
      clearLocalCheckInState(payload)
      setIncomingRequestModalData(null)

      toast.info(
        payload?.countdownReason
          ? `${payload.countdownReason}. Redirecting you to clinic home.`
          : 'You have been checked out. Redirecting you to clinic home.',
      )

      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
      }

      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/mentor-home/my-clinic-associations', { replace: true })
      }, 1400)
    },
    [navigate],
  )

  useEffect(() => {
    const handleSignalingEvent = async (event) => {
      const command = event?.detail?.command
      const payload = normalizeCheckInRequest(event?.detail?.payload)

      switch (command) {
        case SIGNALING_COMMANDS.REFRESH_BOOKINGS:
          break

        case SIGNALING_COMMANDS.USER_CHECKED_OUT:
        case SIGNALING_COMMANDS.FORCED_CHECKOUT:
          handleForcedCheckout(payload)
          break

        case SIGNALING_COMMANDS.VIDEO_CALL_STARTED:
          await handleVideoCallStarted(payload)
          break

        case SIGNALING_COMMANDS.CHECKIN_REQUEST:
        case SIGNALING_COMMANDS.CHECKIN_REQUEST_LEGACY:
          setIncomingRequestModalData({
            ...payload,
            status: payload?.status || 'PENDING',
          })
          break

        case SIGNALING_COMMANDS.CHECKIN_REQUEST_CREATED_LOCAL:
          setOutgoingRequestModalData({
            ...payload,
            status: payload?.status || 'PENDING',
          })
          break

        case SIGNALING_COMMANDS.CHECKIN_REQUEST_COUNTDOWN_STARTED:
          if (payload?.occupyingPractitionerId === mentorProfileData?.id) {
            setIncomingRequestModalData(payload)
          }
          if (
            payload?.requestingPractitionerId === mentorProfileData?.id ||
            outgoingRequestRef.current?.requestId === payload?.requestId
          ) {
            setOutgoingRequestModalData(payload)
          }
          if (
            payload?.occupyingPractitionerId !== mentorProfileData?.id &&
            payload?.requestingPractitionerId !== mentorProfileData?.id &&
            incomingRequestRef.current?.requestId !== payload?.requestId &&
            outgoingRequestRef.current?.requestId !== payload?.requestId
          ) {
            restoreActiveRequests()
          }
          break

        case SIGNALING_COMMANDS.CHECKIN_REQUEST_DENIED:
          setOutgoingRequestModalData({
            ...payload,
            status: 'DENIED',
          })
          toast.error(
            payload?.initiatedBy
              ? `${payload.initiatedBy} declined the checkout request.`
              : 'The checked-in mentor declined the checkout request.',
          )
          break

        case SIGNALING_COMMANDS.CHECKIN_REQUEST_ROOM_AVAILABLE:
          setOutgoingRequestModalData({
            ...payload,
            status: 'ROOM_AVAILABLE',
            roomAvailable: true,
          })
          toast.success(`${payload?.chamberRoomTitle || 'The room'} is now available.`)
          break

        default:
          console.info('Unknown mentor signaling command:', command)
      }
    }

    return addWindowSignalingEventListener({
      dispatchEventKey: DISPATCH_EVENT_KEYS.mentorSignalingEvent,
      handler: handleSignalingEvent,
    })
  }, [handleForcedCheckout, mentorProfileData?.id, restoreActiveRequests])

  useEffect(() => {
    const handleLocalCheckIn = () => {
      setOutgoingRequestModalData(null)
    }

    const handleLocalCheckOut = () => {
      setIncomingRequestModalData(null)
    }

    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handleLocalCheckIn)
    window.addEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, handleLocalCheckOut)

    return () => {
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedIn, handleLocalCheckIn)
      window.removeEventListener(DISPATCH_EVENT_KEYS.userCheckedOut, handleLocalCheckOut)
    }
  }, [])

  const handleVideoCallStarted = async (payload) => {
    try {
      const { bookedEventId, bookedEventSlug, status, timestamp } = payload || {}

      if (!bookedEventId && !bookedEventSlug) {
        console.error('Video call signal missing bookedEventId or bookedEventSlug')
        return
      }

      let bookedEventInfo = null
      const storageKeys = ['bookedEventInfo', 'currentBookedEventInfo', 'sessionBookedEventInfo']

      for (const key of storageKeys) {
        const stored = localStorage.getItem(key) || sessionStorage.getItem(key)
        if (!stored) continue

        try {
          const parsed = JSON.parse(stored)
          if (
            parsed?.id === bookedEventId ||
            parsed?.slug === bookedEventSlug ||
            parsed?.slug === bookedEventId
          ) {
            bookedEventInfo = parsed
            break
          }
        } catch (error) {
          console.warn(`Failed to parse stored ${key}:`, error)
        }
      }

      if (!bookedEventInfo) {
        const response = await sessionService.getBookedEventDetails(
          bookedEventId || bookedEventSlug,
          Boolean(bookedEventSlug),
        )

        if (!response.success) {
          toast.error('Could not load video call details')
          return
        }

        bookedEventInfo = response.bookedEvent
      }

      setVideoCallModalData({
        bookedEventInfo,
        status: status || 'started',
        receivedAt: timestamp || new Date().toISOString(),
      })
    } catch (error) {
      console.error('Error handling video call signal:', error)
      toast.error('Error processing video call notification')
    }
  }

  const closeVideoCallModal = () => {
    setVideoCallModalData(null)
  }

  const handleIncomingRequestCheckout = async () => {
    const activeRequest = incomingRequestRef.current
    if (!activeRequest?.requestId) {
      return
    }

    setIncomingRequestLoading(true)

    try {
      const { request, response } = await respondToEmrCheckInRequest({
        requestId: activeRequest.requestId,
        status: 'ACKNOWLEDGED',
      })

      if (request) {
        setIncomingRequestModalData(request)
        toast.info('Checkout countdown started.')
        return
      }

      toast.error(getCheckInRequestErrorMessage(response, 'Could not start the checkout countdown.'))
    } catch (error) {
      console.error('Error acknowledging check-in request:', error)
      toast.error('Could not start the checkout countdown.')
    } finally {
      setIncomingRequestLoading(false)
    }
  }

  const handleIncomingRequestIgnore = async () => {
    const activeRequest = incomingRequestRef.current
    if (!activeRequest?.requestId) {
      return
    }

    setIncomingRequestLoading(true)

    try {
      const { request, response } = await respondToEmrCheckInRequest({
        requestId: activeRequest.requestId,
        status: 'DENIED',
      })

      if (request || response?.status === 200) {
        setIncomingRequestModalData(null)
        toast.info('You chose to stay checked in.')
        return
      }

      toast.error(getCheckInRequestErrorMessage(response, 'Could not decline the request.'))
    } catch (error) {
      console.error('Error declining check-in request:', error)
      toast.error('Could not decline the request.')
    } finally {
      setIncomingRequestLoading(false)
    }
  }

  const closeIncomingRequestModal = () => {
    const activeRequest = incomingRequestRef.current
    if (incomingRequestLoading || activeRequest?.status === 'PENDING' || activeRequest?.status === 'CHECKOUT_PENDING') {
      return
    }

    setIncomingRequestModalData(null)
  }

  const closeOutgoingRequestModal = () => {
    const activeRequest = outgoingRequestRef.current
    if (
      activeRequest?.status === 'PENDING' ||
      activeRequest?.status === 'CHECKOUT_PENDING'
    ) {
      return
    }

    setOutgoingRequestModalData(null)
  }

  const openRequestedRoom = () => {
    const activeRequest = outgoingRequestRef.current
    if (!activeRequest) {
      navigate('/mentor-home/my-clinic-associations')
      return
    }

    navigate(getRouteForRequest(activeRequest))
    setOutgoingRequestModalData(null)
  }

  return {
    videoCallModalData,
    closeVideoCallModal,
    incomingRequestModalData,
    outgoingRequestModalData,
    incomingRequestLoading,
    requestClockNow,
    closeIncomingRequestModal,
    closeOutgoingRequestModal,
    handleIncomingRequestCheckout,
    handleIncomingRequestIgnore,
    openRequestedRoom,
  }
}
